import {
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Chip,
} from '@mui/material';
import moment from 'moment';
import { FC, useState } from 'react';

import Utils from '../../../../../components/Utils';

import { Campaign, CampaignChangeRequest, Platform } from '../../../../../types/Campaign';
import { ZipCodeDisplay } from '../../../../campaignManager/campaignEdit/CampaignSummaryV2';
import { useUserContext } from '../../../../../hooks/Contexts/UserContext';
import CampaignAudienceTypeSummary from '../../../../../features/Campaign/CampaignAudienceTypeSummary';
import CampaignCreativeTypeSummary from '../../../../../features/Campaign/CampaignCreativeTypeSummary';
import CampaignFrequencySummary from '../../../../../features/Campaign/CampaignFrequencySummary';

const emptySymbol: string = ' - ';

const FormatUtils = {
    formatDate: (date?: Date | string): string => (date ? moment(date).format('MMMM D, YYYY') : emptySymbol),
    formatCurrency: (value?: number): string => (value ? (Utils.formatCurrency(value) as string) : emptySymbol),
    formatNumber: (value?: number): string => (value ? (Utils.formatNumber(value) as string) : emptySymbol),
    formatPercent: (value?: number | null): string => (value ? `${value}%` : emptySymbol),
    formatDollar: (value?: number | null): string => (value ? `$${value}` : emptySymbol),
    formatIncluded: (included?: boolean): string =>
        included ? 'Included in Spend / Budget' : 'Excluded from Spend / Budget',
};

const campaignFormatters: Partial<Record<keyof Campaign, (v: any) => string>> = {
    startDate: FormatUtils.formatDate,
    endDate: FormatUtils.formatDate,
    budget: FormatUtils.formatCurrency,
    impressionGoal: FormatUtils.formatNumber,
    zipcodeRadius: FormatUtils.formatNumber,
};

const campaignChangeRequestFormatters: Partial<Record<keyof CampaignChangeRequest, (v: any) => string>> = {
    startDate: FormatUtils.formatDate,
    endDate: FormatUtils.formatDate,
    budget: FormatUtils.formatCurrency,
    impressionGoal: FormatUtils.formatNumber,
    zipcodeRadius: FormatUtils.formatNumber,
};

interface CampaignChangeRequestTableProps {
    campaign: Campaign;
    campaignChangeRequest: CampaignChangeRequest;
}

const CampaignChangeRequestTable: FC<CampaignChangeRequestTableProps> = ({ campaign, campaignChangeRequest }) => {
    const { userContext } = useUserContext();
    const isApproved: boolean = campaignChangeRequest.status === 'Complete';
    const [flightsDialogOpen, setFlightsDialogOpen] = useState(false);
    const isAgencySource: boolean = campaignChangeRequest.source === 'AGENCY';

    const getField = <K extends keyof Campaign & keyof CampaignChangeRequest>(
        key: K,
        opts: { source: 'campaign' | 'campaignChangeRequest' }
    ): string => {
        // Hide all campaign fields once approved
        // as its irrelevant at the present time.
        if (opts.source === 'campaign' && isApproved) {
            return emptySymbol;
        }

        const source = opts.source === 'campaign' ? campaign : campaignChangeRequest;

        const formatter =
            opts.source === 'campaign'
                ? campaignFormatters[key as keyof typeof campaignFormatters]
                : campaignChangeRequestFormatters[key as keyof typeof campaignChangeRequestFormatters];

        if (formatter) {
            return formatter(source[key]);
        }

        const rawValue = source[key];
        if (rawValue !== null && rawValue !== undefined) {
            return String(rawValue);
        }

        return emptySymbol;
    };

    return (
        <>
            <TableContainer component={Paper} variant="outlined">
                <Table
                    sx={{
                        '& .MuiTableHead-root .MuiTableCell-root': {
                            backgroundColor: 'action.hover',
                        },
                        '& .MuiTableHead-root .MuiTableCell-root:last-child': {
                            backgroundColor: 'primary.light',
                            color: 'white',
                        },
                        '& .MuiTableBody-root .MuiTableCell-root:last-child': {
                            backgroundColor: 'rgba(14, 89, 231, 0.04)',
                            color: 'primary.main',
                        },
                        '& .MuiTableRow-root .MuiTableCell-root': {
                            borderRight: 1,
                            borderColor: 'divider',
                        },
                        '& .MuiTableRow-root .MuiTableCell-root:last-child': {
                            borderRight: 0,
                        },
                    }}
                >
                    <TableHead>
                        <TableRow>
                            <TableCell>Level</TableCell>
                            <TableCell>Attribute</TableCell>
                            <TableCell>From</TableCell>
                            <TableCell>To</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(userContext.isAdmin() || userContext.isOpsAdmin()) && (
                            <>
                                {/* Agency Fees */}
                                <TableRow>
                                    <TableCell>Campaign</TableCell>
                                    <TableCell>
                                        {campaign.campaignFee?.agencyFixedCPM != null
                                            ? 'Agency Fixed CPM'
                                            : 'Agency Fee'}
                                    </TableCell>
                                    <TableCell>
                                        {isApproved
                                            ? emptySymbol
                                            : campaign.campaignFee?.agencyFixedCPM != null
                                            ? FormatUtils.formatDollar(campaign.campaignFee?.agencyFixedCPM)
                                            : FormatUtils.formatPercent(campaign.campaignFee?.agencyFee)}
                                    </TableCell>
                                    <TableCell>
                                        {campaignChangeRequest.campaignFee?.agencyFixedCPM != null
                                            ? FormatUtils.formatDollar(
                                                  campaignChangeRequest.campaignFee?.agencyFixedCPM
                                              )
                                            : FormatUtils.formatPercent(campaignChangeRequest.campaignFee?.agencyFee)}
                                    </TableCell>
                                </TableRow>
                                {(campaign.campaignFee?.agencyFixedCPM != null ||
                                    campaignChangeRequest.campaignFee?.agencyFixedCPM != null) && (
                                    <>
                                        <TableRow>
                                            <TableCell></TableCell>
                                            <TableCell>Agency Incremental CPM</TableCell>
                                            <TableCell>
                                                {isApproved
                                                    ? emptySymbol
                                                    : FormatUtils.formatDollar(
                                                          campaign.campaignFee?.agencyIncrementalCPM
                                                      )}
                                            </TableCell>
                                            <TableCell>
                                                {FormatUtils.formatDollar(
                                                    campaignChangeRequest.campaignFee?.agencyIncrementalCPM
                                                )}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell></TableCell>
                                            <TableCell>DSP CPM Cost</TableCell>
                                            <TableCell>
                                                {isApproved
                                                    ? emptySymbol
                                                    : FormatUtils.formatDollar(campaign.campaignFee?.cognitionCPM)}
                                            </TableCell>
                                            <TableCell>
                                                {FormatUtils.formatDollar(
                                                    campaignChangeRequest.campaignFee?.cognitionCPM
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    </>
                                )}

                                {/* Cognition Digital Fees */}
                                <TableRow>
                                    <TableCell></TableCell>
                                    <TableCell>CD Platform Fee</TableCell>
                                    <TableCell>
                                        {isApproved
                                            ? emptySymbol
                                            : FormatUtils.formatDollar(campaign.campaignFee?.cdPlatformFee)}
                                    </TableCell>
                                    <TableCell>
                                        {FormatUtils.formatDollar(campaignChangeRequest.campaignFee?.cdPlatformFee)}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell></TableCell>
                                    <TableCell>CD Platform Fee - Include in Budget</TableCell>
                                    <TableCell>
                                        {isApproved
                                            ? emptySymbol
                                            : FormatUtils.formatIncluded(campaign.campaignFee?.includeCdPlatformFee)}
                                    </TableCell>
                                    <TableCell>
                                        {FormatUtils.formatIncluded(
                                            campaignChangeRequest.campaignFee?.includeCdPlatformFee
                                        )}
                                    </TableCell>
                                </TableRow>
                                {campaign.platform === Platform.AMAZON &&
                                    (!!campaign.campaignFee?.amazonPassthroughUpcharge ||
                                        !!campaignChangeRequest.campaignFee?.amazonPassthroughUpcharge) && (
                                        <>
                                            <TableRow>
                                                <TableCell></TableCell>
                                                <TableCell>Amazon Passthrough Upcharge</TableCell>
                                                <TableCell>
                                                    {isApproved
                                                        ? emptySymbol
                                                        : FormatUtils.formatPercent(
                                                              campaign.campaignFee?.amazonPassthroughUpcharge
                                                          )}
                                                </TableCell>
                                                <TableCell>
                                                    {FormatUtils.formatPercent(
                                                        campaignChangeRequest.campaignFee?.amazonPassthroughUpcharge
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell></TableCell>
                                                <TableCell>Amazon Passthrough - Include in Budget</TableCell>
                                                <TableCell>
                                                    {isApproved
                                                        ? emptySymbol
                                                        : FormatUtils.formatIncluded(
                                                              campaign.campaignFee?.includeAmazonPassthroughUpcharge
                                                          )}
                                                </TableCell>
                                                <TableCell>
                                                    {FormatUtils.formatIncluded(
                                                        campaignChangeRequest.campaignFee
                                                            ?.includeAmazonPassthroughUpcharge
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        </>
                                    )}
                                {campaign.platform === Platform.DV360 &&
                                    (!!campaign.campaignFee?.dv360PassthroughUpchargeOther ||
                                        !!campaignChangeRequest.campaignFee?.dv360PassthroughUpchargeOther) && (
                                        <>
                                            <TableRow>
                                                <TableCell></TableCell>
                                                <TableCell>DV360 Passthrough Upcharge - Other</TableCell>
                                                <TableCell>
                                                    {isApproved
                                                        ? emptySymbol
                                                        : FormatUtils.formatPercent(
                                                              campaign.campaignFee?.dv360PassthroughUpchargeOther
                                                          )}
                                                </TableCell>
                                                <TableCell>
                                                    {FormatUtils.formatPercent(
                                                        campaignChangeRequest.campaignFee?.dv360PassthroughUpchargeOther
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell></TableCell>
                                                <TableCell>
                                                    DV360 Passthrough - Include Other Upcharge in Budget
                                                </TableCell>
                                                <TableCell>
                                                    {isApproved
                                                        ? emptySymbol
                                                        : FormatUtils.formatIncluded(
                                                              campaign.campaignFee?.includeDv360PassthroughUpchargeOther
                                                          )}
                                                </TableCell>
                                                <TableCell>
                                                    {FormatUtils.formatIncluded(
                                                        campaignChangeRequest.campaignFee
                                                            ?.includeDv360PassthroughUpchargeOther
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        </>
                                    )}
                                {campaign.platform === Platform.DV360 &&
                                    (!!campaign.campaignFee?.dv360PassthroughUpchargeYouTube ||
                                        !!campaignChangeRequest.campaignFee?.dv360PassthroughUpchargeYouTube) && (
                                        <>
                                            <TableRow>
                                                <TableCell></TableCell>
                                                <TableCell>DV360 Passthrough Upcharge - YouTube</TableCell>
                                                <TableCell>
                                                    {isApproved
                                                        ? emptySymbol
                                                        : FormatUtils.formatPercent(
                                                              campaign.campaignFee?.dv360PassthroughUpchargeYouTube
                                                          )}
                                                </TableCell>
                                                <TableCell>
                                                    {FormatUtils.formatPercent(
                                                        campaignChangeRequest.campaignFee
                                                            ?.dv360PassthroughUpchargeYouTube
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell></TableCell>
                                                <TableCell>
                                                    DV360 Passthrough - Include YouTube Upcharge in Budget
                                                </TableCell>
                                                <TableCell>
                                                    {isApproved
                                                        ? emptySymbol
                                                        : FormatUtils.formatIncluded(
                                                              campaign.campaignFee
                                                                  ?.includeDv360PassthroughUpchargeYouTube
                                                          )}
                                                </TableCell>
                                                <TableCell>
                                                    {FormatUtils.formatIncluded(
                                                        campaignChangeRequest.campaignFee
                                                            ?.includeDv360PassthroughUpchargeYouTube
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        </>
                                    )}

                                <TableRow>
                                    <TableCell></TableCell>
                                    <TableCell>CD Managed Service</TableCell>
                                    <TableCell>
                                        {isApproved
                                            ? emptySymbol
                                            : FormatUtils.formatPercent(campaign.campaignFee?.cdManagedService)}
                                    </TableCell>
                                    <TableCell>
                                        {FormatUtils.formatPercent(campaignChangeRequest.campaignFee?.cdManagedService)}
                                    </TableCell>
                                </TableRow>
                                {(!!campaign.campaignFee?.otherFee ||
                                    !!campaignChangeRequest.campaignFee?.otherFee) && (
                                    <>
                                        <TableRow>
                                            <TableCell></TableCell>
                                            <TableCell>
                                                {campaignChangeRequest.campaignFee?.otherFeeLabel || 'Other Fee'}
                                            </TableCell>
                                            <TableCell>
                                                {isApproved
                                                    ? emptySymbol
                                                    : FormatUtils.formatDollar(campaign.campaignFee?.otherFee)}
                                            </TableCell>
                                            <TableCell>
                                                {FormatUtils.formatDollar(campaignChangeRequest.campaignFee?.otherFee)}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell></TableCell>
                                            <TableCell>Other Fee - Include in Budget</TableCell>
                                            <TableCell>
                                                {isApproved
                                                    ? emptySymbol
                                                    : FormatUtils.formatIncluded(campaign.campaignFee?.includeOtherFee)}
                                            </TableCell>
                                            <TableCell>
                                                {FormatUtils.formatIncluded(
                                                    campaignChangeRequest.campaignFee?.includeOtherFee
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    </>
                                )}
                            </>
                        )}

                        {!isAgencySource && campaign.flights?.length > 0 && (
                            <TableRow>
                                <TableCell>Flights</TableCell>
                                <TableCell colSpan={3}>
                                    <Button variant="text" size="small" onClick={() => setFlightsDialogOpen(true)}>
                                        View Flights ({campaignChangeRequest.flights?.length || campaign.flights.length}
                                        )
                                    </Button>
                                </TableCell>
                            </TableRow>
                        )}

                        {!isAgencySource && (
                            <>
                                <TableRow>
                                    <TableCell>Campaign Ad Group</TableCell>
                                    <TableCell>Start Date</TableCell>
                                    <TableCell>{getField('startDate', { source: 'campaign' })}</TableCell>
                                    <TableCell>{getField('startDate', { source: 'campaignChangeRequest' })}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell></TableCell>
                                    <TableCell>End Date</TableCell>
                                    <TableCell>{getField('endDate', { source: 'campaign' })}</TableCell>
                                    <TableCell>{getField('endDate', { source: 'campaignChangeRequest' })}</TableCell>
                                </TableRow>

                                <TableRow>
                                    <TableCell></TableCell>
                                    <TableCell>Budget Cap</TableCell>
                                    <TableCell>{getField('budgetCapType', { source: 'campaign' })}</TableCell>
                                    <TableCell>
                                        {getField('budgetCapType', { source: 'campaignChangeRequest' })}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell></TableCell>
                                    <TableCell>Budget</TableCell>
                                    <TableCell>{getField('budget', { source: 'campaign' })}</TableCell>
                                    <TableCell>{getField('budget', { source: 'campaignChangeRequest' })}</TableCell>
                                </TableRow>
                                {(userContext.isAdmin() || userContext.isOpsAdmin()) && (
                                    <TableRow>
                                        <TableCell></TableCell>
                                        <TableCell>DSP Budget</TableCell>
                                        <TableCell>{getField('dspBudget', { source: 'campaign' })}</TableCell>
                                        <TableCell>
                                            {getField('dspBudget', { source: 'campaignChangeRequest' })}
                                        </TableCell>
                                    </TableRow>
                                )}
                                <TableRow>
                                    <TableCell></TableCell>
                                    <TableCell>Impression Goal</TableCell>
                                    <TableCell>{getField('impressionGoal', { source: 'campaign' })}</TableCell>
                                    <TableCell>
                                        {getField('impressionGoal', { source: 'campaignChangeRequest' })}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell></TableCell>
                                    <TableCell>Frequency Cap</TableCell>
                                    <TableCell>
                                        {!isApproved && <CampaignFrequencySummary campaign={campaign} />}
                                    </TableCell>
                                    <TableCell>
                                        <CampaignFrequencySummary
                                            campaign={{
                                                ...campaign,
                                                frequencyMaxImpressions: campaignChangeRequest?.frequencyMaxImpressions,
                                                frequencyType: campaignChangeRequest?.frequencyType,
                                                frequencyTimeUnitCount: campaignChangeRequest?.frequencyTimeUnitCount,
                                                frequencyTimeUnit: campaignChangeRequest?.frequencyTimeUnit,
                                            }}
                                        />
                                    </TableCell>
                                </TableRow>

                                <TableRow>
                                    <TableCell>Geography Targeting</TableCell>
                                    <TableCell>Radius</TableCell>
                                    <TableCell>{getField('zipcodeRadius', { source: 'campaign' })}</TableCell>
                                    <TableCell>
                                        {getField('zipcodeRadius', { source: 'campaignChangeRequest' })}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell></TableCell>
                                    <TableCell>Zip Codes</TableCell>
                                    <TableCell>
                                        {!isApproved && (
                                            <ZipCodeDisplay modified={false} zipcodes={campaign.zipcodes} />
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <ZipCodeDisplay
                                            modified={campaign?.zipcodes !== campaignChangeRequest?.zipcodes}
                                            zipcodes={campaignChangeRequest?.zipcodes}
                                        />
                                    </TableCell>
                                </TableRow>

                                <TableRow>
                                    <TableCell>Audience Targeting</TableCell>
                                    <TableCell>Audience Type</TableCell>
                                    <TableCell>
                                        {!isApproved && <CampaignAudienceTypeSummary campaign={campaign} />}
                                    </TableCell>
                                    <TableCell>
                                        <CampaignAudienceTypeSummary
                                            campaign={{
                                                ...campaign,
                                                audienceType: campaignChangeRequest?.audienceType,
                                            }}
                                        />
                                    </TableCell>
                                </TableRow>

                                <TableRow>
                                    <TableCell>Additional Information</TableCell>
                                    <TableCell>Email for Approval</TableCell>
                                    <TableCell>{getField('emailForApproval', { source: 'campaign' })}</TableCell>
                                    <TableCell>
                                        {getField('emailForApproval', { source: 'campaignChangeRequest' })}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell></TableCell>
                                    <TableCell>Call to Action</TableCell>
                                    <TableCell>{getField('callToActions', { source: 'campaign' })}</TableCell>
                                    <TableCell>
                                        {getField('callToActions', { source: 'campaignChangeRequest' })}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell></TableCell>
                                    <TableCell>Landing Page</TableCell>
                                    <TableCell>{getField('landingPage', { source: 'campaign' })}</TableCell>
                                    <TableCell>
                                        {getField('landingPage', { source: 'campaignChangeRequest' })}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell></TableCell>
                                    <TableCell>Inventory Provider</TableCell>
                                    <TableCell>{getField('inventoryProvider', { source: 'campaign' })}</TableCell>
                                    <TableCell>
                                        {getField('inventoryProvider', { source: 'campaignChangeRequest' })}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell></TableCell>
                                    <TableCell>Additional Comments</TableCell>
                                    <TableCell>{getField('additionalComments', { source: 'campaign' })}</TableCell>
                                    <TableCell>
                                        {getField('additionalComments', { source: 'campaignChangeRequest' })}
                                    </TableCell>
                                </TableRow>

                                <TableRow>
                                    <TableCell>Creative</TableCell>
                                    <TableCell>Creative Type</TableCell>
                                    <TableCell>
                                        <CampaignCreativeTypeSummary campaign={campaign} />
                                    </TableCell>
                                    <TableCell>
                                        <CampaignCreativeTypeSummary campaign={campaign} />
                                    </TableCell>
                                </TableRow>
                            </>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={flightsDialogOpen} onClose={() => setFlightsDialogOpen(false)} maxWidth="lg" fullWidth>
                <DialogTitle>Flight Changes</DialogTitle>
                <DialogContent>
                    <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Flight</TableCell>
                                    <TableCell colSpan={3} align="center" sx={{ backgroundColor: 'action.hover' }}>
                                        Current
                                    </TableCell>
                                    <TableCell
                                        colSpan={3}
                                        align="center"
                                        sx={{ backgroundColor: 'primary.light', color: 'white' }}
                                    >
                                        Proposed
                                    </TableCell>
                                    <TableCell>Status</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell></TableCell>
                                    <TableCell sx={{ backgroundColor: 'action.hover' }}>Start Date</TableCell>
                                    <TableCell sx={{ backgroundColor: 'action.hover' }}>End Date</TableCell>
                                    <TableCell align="right" sx={{ backgroundColor: 'action.hover' }}>
                                        Budget
                                    </TableCell>
                                    <TableCell sx={{ backgroundColor: 'rgba(14, 89, 231, 0.04)' }}>
                                        Start Date
                                    </TableCell>
                                    <TableCell sx={{ backgroundColor: 'rgba(14, 89, 231, 0.04)' }}>End Date</TableCell>
                                    <TableCell align="right" sx={{ backgroundColor: 'rgba(14, 89, 231, 0.04)' }}>
                                        Budget
                                    </TableCell>
                                    <TableCell></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(() => {
                                    const ccrFlights = campaignChangeRequest.flights || [];
                                    const currentFlights = campaign.flights || [];

                                    const totalFlights = ccrFlights.length;
                                    return ccrFlights.map((ccrFlight: any, index: number) => {
                                        const matchingFlight = currentFlights.find(
                                            (f) => f.id === ccrFlight.campaignFlightId
                                        );
                                        const isNew = !ccrFlight.campaignFlightId;

                                        return (
                                            <TableRow key={ccrFlight.id || index}>
                                                <TableCell>
                                                    {totalFlights - index}
                                                    {isNew && (
                                                        <Chip label="New" color="success" size="small" sx={{ ml: 1 }} />
                                                    )}
                                                </TableCell>
                                                <TableCell sx={{ backgroundColor: 'action.hover' }}>
                                                    {matchingFlight
                                                        ? moment(matchingFlight.startDateTime).format('MMM D, YYYY')
                                                        : emptySymbol}
                                                </TableCell>
                                                <TableCell sx={{ backgroundColor: 'action.hover' }}>
                                                    {matchingFlight
                                                        ? moment(matchingFlight.endDateTime).format('MMM D, YYYY')
                                                        : emptySymbol}
                                                </TableCell>
                                                <TableCell align="right" sx={{ backgroundColor: 'action.hover' }}>
                                                    {matchingFlight
                                                        ? FormatUtils.formatCurrency(matchingFlight.budgetAmount)
                                                        : emptySymbol}
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        backgroundColor: 'rgba(14, 89, 231, 0.04)',
                                                        color: 'primary.main',
                                                        fontWeight:
                                                            matchingFlight &&
                                                            moment(matchingFlight.startDateTime).format(
                                                                'YYYY-MM-DD'
                                                            ) !== moment(ccrFlight.startDateTime).format('YYYY-MM-DD')
                                                                ? 600
                                                                : 400,
                                                    }}
                                                >
                                                    {moment(ccrFlight.startDateTime).format('MMM D, YYYY')}
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        backgroundColor: 'rgba(14, 89, 231, 0.04)',
                                                        color: 'primary.main',
                                                        fontWeight:
                                                            matchingFlight &&
                                                            moment(matchingFlight.endDateTime).format('YYYY-MM-DD') !==
                                                                moment(ccrFlight.endDateTime).format('YYYY-MM-DD')
                                                                ? 600
                                                                : 400,
                                                    }}
                                                >
                                                    {moment(ccrFlight.endDateTime).format('MMM D, YYYY')}
                                                </TableCell>
                                                <TableCell
                                                    align="right"
                                                    sx={{
                                                        backgroundColor: 'rgba(14, 89, 231, 0.04)',
                                                        color: 'primary.main',
                                                        fontWeight:
                                                            matchingFlight &&
                                                            matchingFlight.budgetAmount !== ccrFlight.budgetAmount
                                                                ? 600
                                                                : 400,
                                                    }}
                                                >
                                                    {FormatUtils.formatCurrency(ccrFlight.budgetAmount)}
                                                </TableCell>
                                                <TableCell>
                                                    {(() => {
                                                        const now = new Date();
                                                        const start = new Date(ccrFlight.startDateTime);
                                                        const end = new Date(ccrFlight.endDateTime);
                                                        const isLive = start <= now && end >= now;
                                                        const isUpcoming = start > now;
                                                        const status = isLive
                                                            ? 'Live'
                                                            : isUpcoming
                                                            ? 'Upcoming'
                                                            : 'Completed';
                                                        const statusColor = isLive
                                                            ? 'success'
                                                            : isUpcoming
                                                            ? 'info'
                                                            : 'default';
                                                        return <Chip label={status} color={statusColor} size="small" />;
                                                    })()}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    });
                                })()}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setFlightsDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export type { CampaignChangeRequestTableProps };
export default CampaignChangeRequestTable;
